/* ══════════════════════════════════════════════════
 *  Offboard user — one action for a leaver
 *  Form (reason, who takes over, type the username) → results screen
 *  with a tick or cross per step and what is still to do outside Junior.
 * ══════════════════════════════════════════════════ */
import { useState } from 'react';
import css from '../AdminPage.module.css';
import { useOffboardUser } from '../../../hooks/useAdmin';
import { roleLabel } from '../../../utils/roleHierarchy';
import { apiErrorMessage } from '../../../utils/apiError';
import type { AdminUser, OffboardResult } from '../../../types/admin';

interface Props {
  user: AdminUser;
  /** Everyone on the Users screen; active users other than the leaver can take over the work. */
  users: AdminUser[];
  onClose: () => void;
  /** Called when every step succeeded, e.g. to show a confirmation message. */
  onOffboarded?: (result: OffboardResult) => void;
}

export default function OffboardUserModal({ user, users, onClose, onOffboarded }: Props) {
  const offboard = useOffboardUser();
  const [reason, setReason] = useState('');
  const [handoverTo, setHandoverTo] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [result, setResult] = useState<OffboardResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const colleagues = users.filter(
    (u) => u.username !== user.username && u.status !== 'deactivated',
  );
  const ready = reason.trim() !== '' && handoverTo !== '' && confirmText === user.username;

  async function handleOffboard() {
    setError(null);
    try {
      const outcome = await offboard.mutateAsync({
        username: user.username,
        reason: reason.trim(),
        handoverTo,
      });
      setResult(outcome);
      if (outcome.success) onOffboarded?.(outcome);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to offboard user'));
    }
  }

  return (
    <div className={css.modalBackdrop} onClick={onClose}>
      <div
        className={css.modal}
        role="dialog"
        aria-label={`Offboard ${user.username}`}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640 }}
      >
        <div className={css.modalHeader}>
          <h3 className={css.modalTitle}>
            {!result
              ? `Offboard user: ${user.username}`
              : result.success
                ? `User offboarded: ${user.username}`
                : `Offboarding incomplete: ${user.username}`}
          </h3>
          <button className={css.modalClose} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {!result ? (
          <>
            <div className={css.modalBody}>
              <p style={{ marginTop: 0, color: 'var(--gray-600)' }}>
                This locks <strong>{user.username}</strong> ({roleLabel(user.role)}) out of Junior
                on every device, records and then removes their role, permissions and groups, and
                hands their open tasks and cases to the colleague you choose. Everything is recorded
                in the audit log.
              </p>

              <div className={css.formGroup}>
                <label className={css.formLabel} htmlFor="offboard-reason">
                  Reason *
                </label>
                <textarea
                  id="offboard-reason"
                  className={css.formTextarea}
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="For example: left the firm on 31 October"
                />
              </div>

              <div className={css.formGroup}>
                <label className={css.formLabel} htmlFor="offboard-handover">
                  Who takes over their open tasks and cases? *
                </label>
                <select
                  id="offboard-handover"
                  className={css.formSelect}
                  value={handoverTo}
                  onChange={(e) => setHandoverTo(e.target.value)}
                >
                  <option value="">Choose a colleague…</option>
                  {colleagues.map((u) => (
                    <option key={u.username} value={u.username}>
                      {u.full_name ? `${u.full_name} (${u.username})` : u.username}
                    </option>
                  ))}
                </select>
              </div>

              <div className={css.formGroup}>
                <label className={css.formLabel} htmlFor="offboard-confirm">
                  Type <strong>{user.username}</strong> to confirm *
                </label>
                <input
                  id="offboard-confirm"
                  className={css.formInput}
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  autoComplete="off"
                />
              </div>

              {error && (
                <p role="alert" className={css.badgeError} style={{ display: 'block', padding: 8 }}>
                  {error}
                </p>
              )}
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={onClose}>
                Cancel
              </button>
              <button
                className={css.btnDanger}
                disabled={!ready || offboard.isPending}
                onClick={handleOffboard}
              >
                {offboard.isPending ? 'Offboarding…' : 'Offboard user'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={css.modalBody}>
              <p style={{ marginTop: 0 }}>
                {result.success ? (
                  <span className={css.badgeSuccess}>Every step completed</span>
                ) : (
                  <span className={css.badgeWarning}>
                    Some steps failed — press Retry failed steps
                  </span>
                )}
              </p>

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px' }}>
                {result.steps.map((s) => (
                  <li key={s.step} style={{ display: 'flex', gap: 8, padding: '6px 0' }}>
                    <span aria-label={s.ok ? 'done' : 'failed'} style={{ fontWeight: 700 }}>
                      {s.ok ? '✓' : '✗'}
                    </span>
                    <span>
                      <strong>{s.label}</strong>
                      <br />
                      <span style={{ color: 'var(--gray-500)', fontSize: 'var(--font-size-sm)' }}>
                        {s.detail}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>

              <h4 style={{ margin: '0 0 8px' }}>Still to do outside Junior</h4>
              <ul style={{ margin: '0 0 8px', paddingLeft: 20 }}>
                {result.outside_junior.map((item) => (
                  <li key={item.system} style={{ marginBottom: 6 }}>
                    <strong>{item.system}:</strong> {item.action}
                  </li>
                ))}
              </ul>
              <p style={{ color: 'var(--gray-500)', fontSize: 'var(--font-size-sm)', margin: 0 }}>
                Their previous access ({roleLabel(result.previous_access.role)},{' '}
                {result.previous_access.permissions.length} permission(s),{' '}
                {result.previous_access.groups.length} group(s)) is kept in the audit record.
              </p>
            </div>
            <div className={css.modalFooter}>
              {!result.success && (
                <button
                  className={css.btnSecondary}
                  disabled={offboard.isPending}
                  onClick={handleOffboard}
                >
                  Retry failed steps
                </button>
              )}
              <button className={css.btnPrimary} onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
